import apiRequest from "./apiRequest";

import { S3UploadResponse, S3EtagResponses } from "../types/types";

import IndexedDBManager from "src/common/utils/IndexedDBManager";

import {
  DB_NAME,
  DB_VERSION,
  FILE_STORE_NAME,
  FILE_STORE_CONFIG,
} from "src/config/db/config";

export default class UploadFile {
  private filePassword?: string | null;
  public progress = 0;
  public status = 0;
  private verifyData: {
    type: string;
    value: string;
    delete_previous?: boolean;
  } = {
    type: "",
    value: "",
    delete_previous: false,
  };

  constructor(
    file: File,
    progressSetFunc?: (progress: number) => void,
    filePassword?: string | null,
    data?: { type: string; value: string; delete_previous?: boolean },
    callback?: ((data: { source: string; hash: string }) => void) | undefined
  ) {
    this.filePassword = filePassword ? filePassword : null;

    if (data) {
      this.verifyData = data;
    }
    this.initUpload(file, progressSetFunc, callback);
  }

  async getLocalFile(type: string, value: string) {
    try {
      const dbManager = new IndexedDBManager(DB_NAME, DB_VERSION, [
        FILE_STORE_CONFIG,
      ]);
      await dbManager.init();

      const results: any[] = await dbManager.findByCompoundIndex(
        FILE_STORE_NAME,
        "type_value",
        [type, value]
      );

      return results[0]?.file;
    } catch (error) {
      return null;
    }
  }

  private async sendUploadComplete(
    hash: string,
    etagResponses?: S3EtagResponses[],
    progressSetFunc?: (progress: number) => void,
    callback?: ((data: { source: string; hash: string }) => void) | undefined
  ) {
    new apiRequest("file/verify_upload", true, true).send(
      {
        hash: hash,
        filePassword: this.filePassword,
        etag_response: etagResponses,
        data: this.verifyData,
      },
      (data: {
        status: string;
        message: string;
        data: { hash: string; source: string };
      }) => {
        if (data.status === "success") {
          this.status = 1;
          this.progress = 100;
          if (progressSetFunc) {
            progressSetFunc(100);
          }

          if (callback) {
            callback(data.data);
          }
        } else {
          this.status = this.progress = -1;
          if (progressSetFunc) {
            progressSetFunc(-1);
          }
        }
      }
    );
  }

  private async oneTimeUpload(
    url: string,
    file: File,
    hash: string,
    progressSetFunc?: (progress: number) => void,
    callback?: ((data: { source: string; hash: string }) => void) | undefined
  ) {
    this.progress = 0;
    if (progressSetFunc) {
      progressSetFunc(0);
    }

    const response = await fetch(url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response.ok) {
      this.progress = 99;
      if (progressSetFunc) {
        progressSetFunc(99);
      }

      this.sendUploadComplete(hash, undefined, progressSetFunc, callback);
    } else {
      this.progress = -1;
      if (progressSetFunc) {
        progressSetFunc(-1);
      }
    }
  }

  private async largeFileUpload(
    file: File,
    segments: S3UploadResponse[],
    chunkSize: number,
    hash: string,
    progressSetFunc?: (progress: number) => void,
    callback?: ((data: { source: string; hash: string }) => void) | undefined
  ) {
    this.progress = 0;
    if (progressSetFunc) {
      progressSetFunc(0);
    }

    const totalParts = Math.ceil(file.size / chunkSize);

    const uploadPromises = segments.map((segment, index) => {
      const start = index * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const fileChunk = file.slice(start, end);

      if (progressSetFunc) {
        const progress = ((index + 1) / totalParts) * 100;
        this.progress = progress;
        progressSetFunc(progress >= 100 ? 99 : progress);
      }

      return fetch(segment.url, {
        method: "PUT",
        body: fileChunk,
        headers: {
          "Content-Type": "application/octet-stream",
        },
      });
    });

    const responses = await Promise.all(uploadPromises);

    if (responses.every((response) => response.ok)) {
      const etagResponses = segments.map((urlObj, i) => ({
        ETag: responses[i].headers.get("ETag"),
        PartNumber: urlObj.partNumber,
      }));

      this.sendUploadComplete(hash, etagResponses, progressSetFunc, callback);
    } else {
      this.progress = -1;
      if (progressSetFunc) {
        progressSetFunc(-1);
      }
    }
  }

  private getFileTypeByName(name: string) {
    const lastDotIndex = name.lastIndexOf(".");
    if (lastDotIndex > 0 && lastDotIndex < name.length - 1) {
      return name.substring(lastDotIndex + 1).toLowerCase();
    }

    return null;
  }

  public async initUpload(
    file: File,
    progressSetFunc?: (progress: number) => void,
    callback?: ((data: { source: string; hash: string }) => void) | undefined
  ) {
    new apiRequest("file/get_upload_url", true, true).send(
      {
        file_size: file.size,
        file_type: file.type ? file.type : this.getFileTypeByName(file.name),
      },
      (data: {
        status: string;
        hash: string;
        data: S3UploadResponse[];
        file_max_size: number;
      }) => {
        if (data.status === "success") {
          if (data.data.length > 1) {
            this.largeFileUpload(
              file,
              data.data,
              data.file_max_size,
              data.hash,
              progressSetFunc,
              callback
            );
          } else {
            this.oneTimeUpload(
              data.data[0].url,
              file,
              data.hash,
              progressSetFunc,
              callback
            );
          }
        }
      }
    );
  }

  public async uploadComplete() {
    return new Promise((resolve, reject) => {
      setInterval(() => {
        if (this.status === 1) {
          resolve(true);
        } else if (this.status === -1) {
          reject(false);
        }
      }, 500);
    });
  }
}
